import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { ClipboardList, Eye, Check, Loader2 } from "lucide-react";
import DigestPreviewDialog from "./DigestPreviewDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardDigestCTA({ user }) {
  const { t } = useTranslation();
  const isAdmin = user?.role === "system_admin" || user?.role === "govt_admin";
  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const fetchPrefs = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const r = await axios.get(`${API}/admin/digest/me`, { withCredentials: true });
      setPrefs(r.data);
    } catch { /* non-admin or transient — stay hidden */ }
  }, [isAdmin]);

  useEffect(() => { fetchPrefs(); }, [fetchPrefs]);

  const subscribe = async () => {
    if (!prefs) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/digest/me`, { opt_in: true }, { withCredentials: true });
      setPrefs(r.data);
      toast.success(t("digest.subscribed_dashboard_toast"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("digest.could_not_subscribe"));
    } finally {
      setBusy(false);
    }
  };

  if (!isAdmin || !prefs) return null;

  // Subscribed → small badge only (saves sidebar real-estate)
  if (prefs.opt_in) {
    return (
      <div className="mt-3" data-testid="digest-sidebar-subscribed">
        <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm bg-emerald-50 border border-emerald-200 text-emerald-700">
          <Check className="w-3 h-3" />
          <span className="text-[10px] font-bold font-body uppercase tracking-wider">{t("digest.sidebar_on")}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 border border-gray-200 rounded-sm p-3 bg-white" data-testid="digest-sidebar-cta">
      <div className="flex items-start gap-2 mb-2">
        <ClipboardList className="w-3.5 h-3.5 text-gray-700 mt-0.5 shrink-0" />
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-gray-700">{t("digest.sidebar_label")}</p>
          <p className="text-[10px] text-gray-500 font-body mt-0.5 leading-snug">
            {t("digest.sidebar_hint")}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={subscribe}
          disabled={busy}
          data-testid="digest-sidebar-subscribe"
          className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-sm text-[10px] font-bold font-body bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : t("digest.subscribe")}
        </button>
        <button
          type="button"
          onClick={() => setPreviewOpen(true)}
          data-testid="digest-sidebar-preview"
          className="inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-sm text-[10px] font-bold font-body border border-gray-300 hover:bg-gray-50 text-gray-700"
        >
          <Eye className="w-3 h-3" /> {t("digest.preview")}
        </button>
      </div>

      <DigestPreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        email={prefs.email}
        showSubscribe={!prefs.opt_in}
        onSubscribe={async () => { await subscribe(); setPreviewOpen(false); }}
        subscribing={busy}
        testIdPrefix="digest-sidebar-preview"
      />
    </div>
  );
}
